import { useEffect, useState } from "react";

import { Button, Field } from "@/components/kit";
import { useToast } from "@/components/toast";
import { api } from "@/lib/api";

const toNum = (v) => (v.trim() === "" ? null : Number(v.replace(/[^0-9.]/g, "")) || null);

export function DetailsForm({ p, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ address: "", price: "", beds: "", baths: "", sqft: "", highlights: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      address: p.address, price: p.price ?? "", beds: p.beds?.toString() ?? "", baths: p.baths?.toString() ?? "",
      sqft: p.sqft?.toString() ?? "", highlights: p.highlights.join(", "),
    });
  }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps -- seed once per project; refetches must not wipe typing

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api(`/projects/${p.id}`, {
        method: "PATCH",
        body: {
          address: form.address.trim(), price: form.price.trim() || null, beds: toNum(form.beds), baths: toNum(form.baths),
          sqft: toNum(form.sqft), highlights: form.highlights.split(",").map((h) => h.trim()).filter(Boolean),
        },
      });
      onSaved();
      toast("Details saved", "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="flex flex-col gap-4 rounded-[20px] border border-border bg-card p-5 lg:p-6" data-testid="details-form">
      <h2 className="text-xl font-medium">Property details</h2>
      <Field id="details-address" label="Address" value={form.address} onChange={set("address")} data-testid="details-address-input" />
      <Field id="details-price" label="Price" value={form.price} onChange={set("price")} placeholder="$850,000" data-testid="details-price-input" />
      <div className="grid grid-cols-[1fr_1fr_1.3fr] gap-3">
        <Field id="details-beds" label="Beds" value={form.beds} onChange={set("beds")} inputMode="decimal" data-testid="details-beds-input" />
        <Field id="details-baths" label="Baths" value={form.baths} onChange={set("baths")} inputMode="decimal" data-testid="details-baths-input" />
        <Field id="details-sqft" label="Sq ft" value={form.sqft} onChange={set("sqft")} inputMode="numeric" data-testid="details-sqft-input" />
      </div>
      <Field id="details-highlights" label="Highlights (comma separated)" value={form.highlights} onChange={set("highlights")}
        placeholder="pool, renovated kitchen" data-testid="details-highlights-input" />
      <Button type="submit" title="Save details" variant="secondary" loading={saving} data-testid="details-save-button" />
    </form>
  );
}
